import type { TruckClassInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber } from 'antd';
import { useEffect } from 'react';
import type { TruckClass } from '../../../reference-data.api';

interface TruckClassFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: TruckClass;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: TruckClassInput) => Promise<boolean>;
  onClose: () => void;
}

/** One drawer for both create and edit — "edit every field" means there's no separate patch shape. */
export function TruckClassFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: TruckClassFormDrawerProps) {
  const [form] = Form.useForm<TruckClassInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '', typicalCapacityTonnes: undefined as unknown as number });
  }, [open, editing, form]);

  async function submit(values: TruckClassInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit truck class' : 'New truck class'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<TruckClassInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="typicalCapacityTonnes" label="Typical capacity (tonnes)" rules={[{ required: true, message: 'Enter a capacity' }]}>
          <InputNumber className="w-full" min={0.01} step={0.5} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create truck class'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
